import styled from 'styled-components';
import { Circle } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { SecondaryButton } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { Tooltip } from 'ui/tooltip';
import { SelectOptionsContainer } from 'ui/selects';
import { TableHeader } from 'ui/tableHeader';
import { WebLink2, body2, caption, colors, fontSizes, fontWeights, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const StyledPageHeader = styled(PageHeader)`
  [data-test='page-header-documentation-link'] {
    > svg {
      display: none;
    }
  }
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.threeQuarter};
`;

export const InfoBanner = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: ${spacing.quarter};

  margin: -${spacing.layoutTopPadding} -${spacing.layoutLRPadding} 0 -${spacing.layoutLRPadding};
  padding: ${spacing.threeQuarter};

  ${body2}
  background-color: ${colors.cyan10};
  border: ${spacing.single} solid ${colors.cyan20};
  color: ${colors.info100};
`;

export const Link = styled(WebLink2)`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};

  text-decoration: unset;
  color: ${colors.info100};
  border-bottom: ${spacing.single} solid ${colors.info100};
`;

export const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding-top: ${spacing.x3};
  padding-bottom: ${spacing.plusHalf};
`;

export const ContentHeaderTitle = styled.span`
  display: flex;
  align-items: baseline;
  gap: ${spacing.quarter};

  font-size: ${fontSizes.s18};
  font-weight: ${fontWeights.bold};
`;

export const SearchField = styled(InputText)`
  width: ${spacing.x16};

  input::placeholder {
    color: ${colors.grey50};
  }
`;

export const TableWrapper = styled.div`
  margin: 0 -${spacing.layoutLRPadding};
`;

export const StyledTableHeader = styled(TableHeader)<{
  $columnWidths: { [key: string]: number } | null;
}>`
  [role='columnheader'] {
    font-weight: ${fontWeights.semiBold};

    :first-of-type {
      padding-left: ${spacing.layoutLRPadding};
    }
  }

  ${({ $columnWidths }) =>
    $columnWidths &&
    Object.entries($columnWidths).reduce(
      (acc, [fieldId, columnWidth]) =>
        `
          ${acc}
          #header-cell-${fieldId} {
            width: ${pxToRem(columnWidth)};
          }
        `,
      ``,
    )}
`;

export const RowCell = styled.div`
  ${body2}
  display: table-cell;
  box-sizing: border-box;
  padding: ${spacing.base} ${spacing.half};
  vertical-align: middle;

  :first-of-type {
    padding-left: ${spacing.layoutLRPadding};
  }
  :last-of-type {
    padding-right: ${spacing.layoutLRPadding};
  }
`;

export const RegionCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.threeQuarter};

  white-space: nowrap;
`;

export const InstructionsCellWrapper = styled.div<{ displayAsSecondaryText?: boolean }>`
  display: flex;
  flex-direction: column;
  width: fit-content;

  ${({ displayAsSecondaryText }) => displayAsSecondaryText && `color: ${colors.textSecondary};`}
`;

export const FrequencyCellWrapper = styled.div`
  white-space: nowrap;
`;

export const LastModifiedCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const LastModifiedDatesWrapper = styled.div`
  display: flex;
  flex-direction: column;

  white-space: nowrap;
`;

export const StyledTooltip = styled(Tooltip)`
  display: inline-flex;
  cursor: default;
`;

export const SecondaryCaption = styled.span`
  ${caption};
  color: ${colors.textSecondary};
`;

export const FieldColumnTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const StyledOverflowButton = styled(SecondaryButton)<{
  innerRef?: React.Ref<HTMLButtonElement>;
}>`
  padding: ${spacing.half};
`;

export const OverflowOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.threeQuarter};
  padding: ${spacing.half};
`;

export const StyledSelectOptionsContainer = styled(SelectOptionsContainer)`
  max-width: ${spacing.x12};

  li {
    padding: unset;
  }
`;

export const Timeline = styled.div``;

export const TimelinePoint = styled.div`
  display: flex;
  gap: ${spacing.plusHalf};
  position: relative;
`;

export const Line = styled.div`
  border-left: ${spacing.single} solid ${colors.grey15};
  position: absolute;
  height: 100%;
  top: ${spacing.half};
`;

export const StyledCircle = styled(Circle)`
  position: relative;
  top: ${spacing.half};
  left: -4px;
  min-width: 8px;
  z-index: 1;
`;

export const VersionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};

  margin-bottom: ${spacing.double};
`;

export const VersionUpdateUser = styled.div`
  display: flex;
  gap: ${spacing.half};

  ${body2}

  color: ${colors.textSecondary};
`;

export const VersionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};

  margin-top: -${spacing.quarter};
  padding-left: ${spacing.double};

  ${body2}
`;
